import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import Tippy from '@tippyjs/react';
import { format, eachDayOfInterval, isSameDay } from 'date-fns';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Button } from '@nearst/ui';
import { ArrowRightIcon } from '@heroicons/react/20/solid';

import { useShop } from '@services/ShopProvider';
import { getMultipleInsights } from '@services/insights';
import { useInsightsDate } from '@services/InsightsDateProvider';

import { OverviewCard } from '../../components/OverviewCard';
import { EmptyState } from '../../components/EmptyState';
import { StatBadge, comparisonLabel } from '../../components/StatBadge';
import { CARD_STATES, useCardEmptyState } from '../../hooks/useCardEmptyState';

import styles from './PerformanceCard.module.scss';

const SERIES = [
	{ key: 'impressions', label: 'Impressions', color: 'var(--blue)' },
	{ key: 'clicks', label: 'Engagements', color: 'var(--green)' }
];

// Hover explainers — Emily's QA flagged that neither number is defined
// anywhere ("Engagements" doesn't appear on the Insights pages), so spell out
// exactly which surfaces each one sums.
const KPI_TOOLTIPS = {
	impressions:
		'Times your products or store appeared on Google — views of your Local Listings, Local Ads and Google Business Profile on Search and Maps.',
	clicks: 'Times shoppers engaged with you on Google — clicks on your Local Listings and Local Ads, plus website, phone and direction clicks on your Google Business Profile.'
};

const formatCompact = (n) => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
	if (n >= 10_000) return `${Math.round(n / 1_000)}k`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
	return n.toLocaleString();
};

const sumDailyRows = (rows, key) => (rows ?? []).reduce((acc, r) => acc + (r[key] ?? 0), 0);

const pctDelta = (current, previous) => {
	if (!previous) return null;
	return Math.round(((current - previous) / previous) * 1000) / 10;
};

// Bucket daily rows (one per shop per day) into a single daily series for the
// chart. Days with no row come through as zero so the bars stay contiguous.
const buildChartSeries = (rows, startDate, endDate) => {
	const byDate = new Map();
	for (const row of rows ?? []) {
		const acc = byDate.get(row.date) ?? { impressions: 0, clicks: 0 };
		acc.impressions += row.impressions ?? 0;
		acc.clicks += row.clicks ?? 0;
		byDate.set(row.date, acc);
	}
	return eachDayOfInterval({ start: startDate, end: endDate }).map((d) => {
		const key = format(d, 'yyyy-MM-dd');
		const v = byDate.get(key) ?? { impressions: 0, clicks: 0 };
		return { date: key, label: format(d, 'd MMM'), ...v };
	});
};

const PerformanceTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<div className={styles.tooltip}>
			<div className={styles.tooltipLabel}>{label}</div>
			{payload.map((p) => (
				<div key={p.dataKey} className={styles.tooltipRow}>
					<span className={styles.tooltipDot} style={{ background: p.color }} />
					<span>{SERIES.find((s) => s.key === p.dataKey)?.label ?? p.dataKey}</span>
					<span className={styles.tooltipValue}>{p.value.toLocaleString()}</span>
				</div>
			))}
		</div>
	);
};

/**
 * Performance card — Impressions + Engagements (clicks) across the org's Local
 * Listings surfaces, with deltas vs the same-length prior period and a 30-day
 * stacked bar chart. Mirrors the Lovable design (minus the Revenue series).
 *
 * Data: local_listings warehouse table (daily impressions + clicks per shop).
 * Two parallel queries — current period + lookback — fetched via
 * getMultipleInsights so the chart and the deltas share the same rows.
 */
export const PerformanceCard = () => {
	const { data: shops, shop } = useShop();
	const shopIds = shop?.id ? [shop.id] : (shops ?? []).map((s) => s.id);
	const { startDate, endDate, comparison, selectedRange } = useInsightsDate();

	const ranges = {
		current: { from: format(startDate, 'yyyy-MM-dd'), to: format(endDate, 'yyyy-MM-dd') },
		lookback: { from: format(comparison.startDate, 'yyyy-MM-dd'), to: format(comparison.endDate, 'yyyy-MM-dd') }
	};

	// Combine all channel insights with a daily breakdown. Each config defines
	// how to query its table AND how to map rows back to the shared
	// { date, impressions, clicks } shape — so gmb (which uses
	// search_views_* / map_views_* / *_clicks columns instead) can sit on
	// the same chart and feed the same totals.
	const dailyConfigs = useMemo(
		() => [
			{
				table: 'local_listings',
				columns: ['impressions', 'clicks', 'shop_id', 'date'],
				normalise: (r) => ({ date: r.date, impressions: r.impressions ?? 0, clicks: r.clicks ?? 0 })
			},
			{
				table: 'local_ads',
				columns: ['impressions', 'clicks', 'shop_id', 'date'],
				normalise: (r) => ({ date: r.date, impressions: r.impressions ?? 0, clicks: r.clicks ?? 0 })
			},
			{
				table: 'gmb',
				columns: [
					'map_views_desktop',
					'map_views_mobile',
					'search_views_desktop',
					'search_views_mobile',
					'website_clicks',
					'phone_clicks',
					'directions_clicks',
					'shop_id',
					'date'
				],
				normalise: (r) => ({
					date: r.date,
					impressions:
						(r.search_views_desktop ?? 0) +
						(r.search_views_mobile ?? 0) +
						(r.map_views_desktop ?? 0) +
						(r.map_views_mobile ?? 0),
					clicks: (r.website_clicks ?? 0) + (r.phone_clicks ?? 0) + (r.directions_clicks ?? 0)
				})
			}
		],
		[]
	);

	const queries = shopIds.length
		? dailyConfigs.flatMap(({ table, columns }) => [
				{ table, id: shopIds, columns, ...ranges.current },
				{ table, id: shopIds, columns, ...ranges.lookback }
			])
		: null;

	const swrKey = queries ? ['performance-channels', JSON.stringify(queries)] : null;
	const { data: resultSet, isLoading } = useSWR(swrKey, () => getMultipleInsights(queries));

	// queries are in [config0-current, config0-lookback, config1-current, ...] order.
	const currentRows = [];
	const prevRows = [];
	if (resultSet) {
		dailyConfigs.forEach((config, idx) => {
			currentRows.push(...(resultSet[idx * 2] ?? []).map(config.normalise));
			prevRows.push(...(resultSet[idx * 2 + 1] ?? []).map(config.normalise));
		});
	}

	const totals = {
		impressions: sumDailyRows(currentRows, 'impressions'),
		clicks: sumDailyRows(currentRows, 'clicks')
	};
	const prev = {
		impressions: sumDailyRows(prevRows, 'impressions'),
		clicks: sumDailyRows(prevRows, 'clicks')
	};
	const deltas = {
		impressions: pctDelta(totals.impressions, prev.impressions),
		clicks: pctDelta(totals.clicks, prev.clicks)
	};

	const chartData = useMemo(
		() => buildChartSeries(currentRows, startDate, endDate),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[resultSet, startDate, endDate]
	);

	const allEmpty = totals.impressions === 0 && totals.clicks === 0;
	const emptyState = useCardEmptyState({
		cardId: 'performance',
		requiresChannel: 'google',
		// PENDING is disabled — `latestIngest` is "last upload", not "first ever
		// upload", so for any active retailer it's always recent and PENDING
		// would fire on any empty period. Empty data now falls through to
		// NO_TRACTION ("try widening the date range"), which is the right
		// nudge in nearly every case.
		minDaysForData: 0,
		dataIsEmpty: allEmpty
	});

	const showLoading = isLoading && !resultSet;

	return (
		<OverviewCard>
			<div className={styles.headerRow}>
				<h2 className={styles.title}>Performance</h2>
			</div>
			<p className={styles.subtitle}>Reach and engagement across your channels</p>

			{showLoading ? (
				<PerformanceCardSkeleton />
			) : emptyState.state !== CARD_STATES.DATA ? (
				<EmptyState state={emptyState.state} copy={emptyState.copy} ctaLabel={emptyState.ctaLabel} ctaHref={emptyState.ctaHref} />
			) : (
				<div className={styles.periodSection}>
					<h3 className={styles.sectionHeading}>{selectedRange}</h3>
					<div className={styles.kpiRow}>
						<div className={styles.kpi}>
							<div className={styles.figure}>{formatCompact(totals.impressions)}</div>
							<Tippy content={KPI_TOOLTIPS.impressions} delay={[80, 0]}>
								<div className={`${styles.kpiLabel} ${styles.kpiLabelHint}`}>Impressions</div>
							</Tippy>
							{deltas.impressions !== null && (
								<div className={styles.deltaWrap}>
									<StatBadge value={deltas.impressions} positive tooltip={comparisonLabel(comparison)} />
								</div>
							)}
						</div>
						<div className={styles.kpi}>
							<div className={styles.figure}>{formatCompact(totals.clicks)}</div>
							<Tippy content={KPI_TOOLTIPS.clicks} delay={[80, 0]}>
								<div className={`${styles.kpiLabel} ${styles.kpiLabelHint}`}>Engagements</div>
							</Tippy>
							{deltas.clicks !== null && (
								<div className={styles.deltaWrap}>
									<StatBadge value={deltas.clicks} positive tooltip={comparisonLabel(comparison)} />
								</div>
							)}
						</div>
					</div>

					{chartData.length > 1 && (
						<div className={styles.chart}>
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
									<XAxis
										dataKey="label"
										tick={{ fontSize: 10, fill: 'var(--grey-darker)' }}
										tickLine={false}
										axisLine={false}
										interval={Math.max(0, Math.floor(chartData.length / 5) - 1)}
									/>
									<Tooltip cursor={{ fill: 'var(--grey-lightest)' }} content={<PerformanceTooltip />} />
									<Bar dataKey="impressions" stackId="a" fill="var(--blue)" isAnimationActive={false} />
									<Bar dataKey="clicks" stackId="a" fill="var(--green)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
								</BarChart>
							</ResponsiveContainer>
						</div>
					)}

					<div className={styles.legend}>
						{SERIES.map((s) => (
							<div key={s.key} className={styles.legendItem}>
								<span className={styles.legendDot} style={{ background: s.color }} />
								<span>{s.label}</span>
							</div>
						))}
					</div>
				</div>
			)}

			<div className={styles.footer}>
				<Button as={Link} to={shop?.id ? `/insights/${shop.id}/overview` : '/insights/overview'} className={styles.cta}>
					<span>Performance and Attribution</span>
					<ArrowRightIcon className={styles.arrow} aria-hidden="true" />
				</Button>
			</div>
		</OverviewCard>
	);
};

const PerformanceCardSkeleton = () => (
	<div className={styles.periodSection}>
		<div className={`${styles.skeletonBlock} ${styles.skeletonSectionHeading}`} />
		<div className={styles.kpiRow}>
			{[0, 1].map((i) => (
				<div key={i} className={styles.kpi}>
					<div className={`${styles.skeletonBlock} ${styles.skeletonFigure}`} />
					<div className={`${styles.skeletonBlock} ${styles.skeletonLabel}`} />
					<div className={`${styles.skeletonBlock} ${styles.skeletonDelta}`} />
				</div>
			))}
		</div>
		<div className={`${styles.chart} ${styles.skeletonChart}`} />
		<div className={styles.legend}>
			{SERIES.map((s) => (
				<div key={s.key} className={styles.legendItem}>
					<span className={styles.legendDot} style={{ background: 'var(--grey-light)' }} />
					<span className={styles.skeletonLegendLabel} />
				</div>
			))}
		</div>
	</div>
);
